import './style.scss';

import { Col, Row, Space } from 'antd';
import { get } from 'lodash';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withNamespaces } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';

import { handleApiErrors } from '../../api/axiosInstance';
import { TRIP_API } from '../../api/trips';
import { Button, ConfirmModal } from '../../components';
import ErrorBoundary from '../../components/ErrorBoundary';
import FullScreenTripModal from '../../components/Modal/FullScreenTripModal';
import CommonTableFilters from '../../components/shared-ui/CommonTableFilters';
import JourneyDistanceDisplay from '../../components/shared-ui/JourneyDistanceDisplay';
import ProfileNamePicture from '../../components/shared-ui/ProfileNamePicture';
import Table from '../../components/Table';
import ExpandedTripWithAnomalyRow from '../../components/Table/TripsWithAnomaliesTable/ExpandedTripWithAnomalyRow';
import StatusTag from '../../components/Tag/StatusTag';
import Text from '../../components/Text';
import Toast from '../../components/Toast';
import PageContainer from '../../containers/PageContainer';
import withAuthentication from '../../hocs/withAuthentication';
import useAuthUser from '../../hooks/useAuthUser';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import useLocationSearchQueryParser from '../../hooks/useLocationSearchQueryParser';
import useModal from '../../hooks/useModal';
import useTablePagination from '../../hooks/useTablePagination';
import useTableSort from '../../hooks/useTableSort';
import {
  formatPageTitle,
  getUserFullName,
  momentFormat,
  momentTimezone,
  setModalFullscreenWidth,
} from '../../utils/common';
import { formatNumberWithCurrency } from '../../utils/numbers';
import { sortColumnByMomentDate, sortColumnByNumber, sortColumnByStatus } from '../../utils/tables';
import { normalizeTripsSchema } from '../../utils/trips';

/**
 * Trips With Anomalies page
 */
const TripsWithAnomaliesPage = props => {
  const { t } = props;
  const { authUser } = useAuthUser();

  const {
    startDateRange,
    endDateRange,
    page,
    pageSize,
    expandedTrip,
  } = useLocationSearchQueryParser(props.location);
  const {
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
    handleTotalUpdate,
  } = useTablePagination({
    initialPage: page,
    initialPageSize: pageSize,
    useQueryParams: true,
  });

  const [dateRange, setDateRange] = useState(() => [
    startDateRange ? moment(startDateRange) : moment().subtract(1, 'month'),
    endDateRange ? moment(endDateRange) : moment(),
  ]);
  const [
    isTripDetailsModalVisible,
    openTripDetailsModalVisible,
    closeTripDetailsModalVisible,
  ] = useModal();
  const [selectedMapConfig, setSelectedMapConfig] = useState({
    routes: [],
    center: [0, 0],
  });
  const [trips, setTrips] = useState([]);

  const { stringTableSort, handleTableSort } = useTableSort({
    journeyStartTs: -1,
  });
  const [
    isReviewTripModalVisible,
    openReviewTripModal,
    closeReviewTripModal,
    reviewTripModalProps,
  ] = useModal();

  // Reset current page index whenever the filters change
  useDidUpdateEffect(() => {
    handlePageChange(1);
  }, [dateRange, handlePageChange]);

  const [fromDateStr, toDateStr] = useMemo(() => {
    return [
      dateRange[0] ? momentFormat(dateRange[0], 'YYYY-MM-DD') : undefined,
      dateRange[1] ? momentFormat(dateRange[1], 'YYYY-MM-DD') : undefined,
    ];
  }, [dateRange]);

  const tripListQueryKey = useMemo(
    () => [
      'companyTrips',
      fromDateStr,
      toDateStr,
      paginationConfig.current,
      paginationConfig.pageSize,
      stringTableSort,
    ],
    [fromDateStr, toDateStr, paginationConfig, stringTableSort],
  );

  const tripListQuery = useQuery({
    enabled: true,
    queryFn: () =>
      TRIP_API.fetchTrips(
        {
          fromDate: fromDateStr,
          toDate: toDateStr,
          sort: stringTableSort,
          hasAnomaly: true,
        },
        paginationConfig.current,
        paginationConfig.pageSize,
      ),
    queryKey: tripListQueryKey,
    onSuccess: response => {
      const tripsArr = get(response, 'data', []).map(trp => normalizeTripsSchema(trp, undefined));
      setTrips(tripsArr);
      handleTotalUpdate(response.totalCount);

      if (expandedTrip && Array.isArray(tripsArr) && !!tripsArr.length) {
        const tripRow = document.getElementById(expandedTrip);
        if (tripRow) {
          const container = document.querySelector('.main-app-container');
          const y = tripRow.offsetTop + container.offsetTop;
          container.scrollTo({ behavior: 'smooth', top: y });
        }
      }
    },
    onError: error => {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('fetchTripsError'),
        });
      });
    },
  });

  const handleReviewTripWithAnomalyMutation = useMutation(params => TRIP_API.reviewTrip(params), {
    onSuccess: () => {
      closeReviewTripModal();
      tripListQuery.refetch();
      Toast({
        type: 'open',
        message: t('tripReviewSuccess'),
      });
    },
    onError: error => handleApiErrors(error.response),
  });

  const setColumnSorter = (columnKey, sorter = false) => {
    const disabledColumnsOnAsyncSort = ['to_loc', 'from_loc', 'time', 'reject'];

    return !disabledColumnsOnAsyncSort.includes(columnKey);
  };

  const COLUMNS = [
    {
      width: 300,
      title: t('name'),
      key: ['user.firstName', 'user.lastName'].join(','),
      render: (actions, data) => {
        const userFullName = getUserFullName(data);

        if (!userFullName) return '-';

        return (
          <ProfileNamePicture
            t={t}
            linkableUser={data.driverID !== authUser.profile._id}
            user={{
              ...(!userFullName ? { firstName: '-', lastName: '' } : get(data, 'user', {})),
              _id: data.driverID,
            }}
          />
        );
      },
      sorter: setColumnSorter('name', (a, b) => {
        const joinedNameA = getUserFullName(a);
        const joinedNameB = getUserFullName(b);
        return joinedNameA.localeCompare(joinedNameB);
      }),
    },
    {
      width: 200,
      title: t('Group'),
      key: 'group.name',
      dataIndex: ['group', 'name'],
      render: (groupName, data) => groupName || '-',
      sorter: setColumnSorter('groupName', (a, b) => {
        const groupNameA = Array.isArray(a.group)
          ? get(a[0], 'group.name')
          : get(a, 'group.name', '-');
        const groupNameB = Array.isArray(b.group)
          ? get(b[0], 'group.name')
          : get(b, 'group.name', '-');
        return groupNameA.localeCompare(groupNameB);
      }),
    },
    {
      width: 100,
      key: 'journeyStartTs',
      title: t('date'),
      defaultSortOrder: 'descend',
      sorter: setColumnSorter(
        'journeyStartTs',
        sortColumnByMomentDate('date', 'MM/DD/YYYY zz').sorter,
      ),
      render: (actions, data) => {
        return (
          <p style={{ minWidth: 100, maxWidth: 'max-content' }}>
            {data.captureMode === 'Daily-Mileage' ? data.dateNoTimezone : data.date}
          </p>
        );
      },
    },
    {
      width: 190,
      key: 'time',
      title: t('time'),
      sorter: setColumnSorter('time', sortColumnByMomentDate('starttime', 'hh.mm.ss A').sorter),
      render: (actions, data) => {
        if (data.captureMode === 'Daily-Mileage') {
          return '-';
        }

        const timeFrame = [
          momentTimezone(data.journeyStartTs).format('hh:mm a'),
          momentTimezone(data.journeyEndTs).format('hh:mm a'),
        ].join(' - ');

        let time = '';
        if (data.journeyStartTs && data.journeyEndTs) {
          time = `(${moment(data.journeyEndTs).diff(moment(data.journeyStartTs), 'minutes')}min)`;
        }

        return <p style={{ minWidth: 190, maxWidth: 'max-content' }}>{`${timeFrame} ${time}`}</p>;
      },
    },
    {
      width: 100,
      title: t('Amount'),
      key: 'pricing.totalAmount',
      align: 'right',
      sorter: setColumnSorter('amount', sortColumnByNumber('amount').sorter),
      render: (actions, data) => {
        const amount = formatNumberWithCurrency(data.amount, data?.currency);

        return amount;
      },
    },
    {
      width: 160,
      title: t('status'),
      key: 'journeyStatus',
      render: (actions, data) => {
        if (data.status) {
          return <StatusTag status={data.status} />;
        } else {
          return '-';
        }
      },
      sorter: setColumnSorter('journeyStatus', sortColumnByStatus('status').sorter),
    },
    {
      width: 50,
      title: t('currentDistance'),
      align: 'right',
      key: 'journeyDistance',
      dataIndex: 'journeyDistance',
      sorter: setColumnSorter('journeyDistance', sortColumnByNumber('journeyDistance').sorter),
      render: (distance, trip) => (
        <JourneyDistanceDisplay
          t={t}
          distance={distance}
          originalJourneyDistance={get(trip, 'originalJourneyDistance')}
          additionalJourneyDistance={get(trip, 'additionalJourneyDistance')}
        />
      ),
    },
    {
      width: 50,
      title: t('newDistance'),
      align: 'right',
      key: 'newDistance',
      dataIndex: 'newDistance',
      sorter: setColumnSorter('newDistance', sortColumnByNumber('newDistance').sorter),
      render: (distance, trip) => (
        <JourneyDistanceDisplay
          t={t}
          distance={distance}
          originalJourneyDistance={get(trip, 'originalJourneyDistance')}
          additionalJourneyDistance={get(trip, 'additionalJourneyDistance')}
        />
      ),
    },
    {
      title: '',
      key: 'action',
      render: trip => {
        return (
          <Space direction="horizontal" wrap={false} size="small">
            <Button
              block
              size="xs"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                openReviewTripModal({
                  title: t('acceptDistanceAndCoordinatesChanges'),
                  action: 'accept',
                  onOk: () =>
                    handleReviewTripWithAnomalyMutation.mutateAsync({
                      tripId: trip._id,
                      action: 'accept',
                    }),
                });
              }}
            >
              {t('accept')}
            </Button>
            <Button
              block
              size="xs"
              type="secondary"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                openReviewTripModal({
                  title: t('rejectDistanceAndCoordinatesChanges'),
                  action: 'ignore',
                  onOk: () =>
                    handleReviewTripWithAnomalyMutation.mutateAsync({
                      tripId: trip._id,
                      action: 'ignore',
                    }),
                });
              }}
            >
              {t('reject')}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <PageContainer stickyHeader title={t('tripsWithRouteSuggestions')}>
      <Helmet>
        <title>{formatPageTitle(t('tripsWithRouteSuggestions'))}</title>
      </Helmet>
      <Space direction="vertical" size="small">
        <Row gutter={16} align="middle" justify="space-between" wrap={false}>
          <Col flex={1}>
            {tripListQuery.isFetched && (
              <CommonTableFilters
                hiddenFilters={['search', 'type', 'crmMatch', 'status']}
                loggedInUserProfile={authUser}
                dateRangeValue={dateRange}
                onDateRangeChange={range => setDateRange(range)}
                disabledFilters={tripListQuery.isFetching}
              />
            )}
          </Col>
        </Row>

        <Row>
          <Table
            withExpandableRows
            showSearchInput={false}
            loading={tripListQuery.isFetching}
            scroll={{ x: 1200 }}
            columns={COLUMNS}
            dataSource={trips}
            onChange={({ current }, filters, sorters) => {
              handlePageChange(current);
              handleTableSort(sorters?.columnKey, sorters?.order);
            }}
            pagination={{
              pageSize: paginationConfig.pageSize,
              total: paginationConfig.total,
              current: paginationConfig.current,
              onShowSizeChange: handlePageSizeChange,
            }}
            expandedRowRender={trip => {
              return (
                <ErrorBoundary>
                  <ExpandedTripWithAnomalyRow
                    t={t}
                    tripId={trip._id}
                    setSelectedMapConfig={setSelectedMapConfig}
                    openTripDetailsModalVisible={openTripDetailsModalVisible}
                  />
                </ErrorBoundary>
              );
            }}
          />
        </Row>
      </Space>
      <FullScreenTripModal
        visible={isTripDetailsModalVisible}
        mapConfig={selectedMapConfig}
        onCancel={() => {
          closeTripDetailsModalVisible();
          setSelectedMapConfig({ center: [0, 0], routes: [] });
        }}
      />
      <ConfirmModal
        {...reviewTripModalProps}
        disabled={handleReviewTripWithAnomalyMutation.isLoading}
        loading={handleReviewTripWithAnomalyMutation.isLoading}
        width={setModalFullscreenWidth(611)}
        footer={null}
        okText={t('yes')}
        cancelText={t('no')}
        visible={isReviewTripModalVisible}
        onCancel={() => {
          closeReviewTripModal();
        }}
        message={
          <Space direction="vertical" size="middle">
            <>
              <Text>
                {reviewTripModalProps?.action === 'accept'
                  ? t('sureYouWantToAccepDistanceAndCoordinatesChanges')
                  : t('sureYouWantToRejectDistanceAndCoordinatesChanges')}
              </Text>
              <Text>
                {reviewTripModalProps?.action === 'accept'
                  ? t('tripDataWillChange')
                  : t('tripDataWillRemainSame')}
              </Text>
            </>
          </Space>
        }
      />
    </PageContainer>
  );
};

export default withNamespaces()(withAuthentication(TripsWithAnomaliesPage));
